import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { SapTableCopySource } from 'app/model/pipeline/copysource/sap-table.copysource';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { stringIsNullOrEmpty, string2UnicodeSingle, unicode2StrSingle } from 'app/shared/utils/string.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { columnDelimiterWithoutNoneV2Options } from 'app/shared/components/fileformat/fileformat.constants';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SapTableCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        batchSize: '*',
        rowCount: '*',
        rowSkips: '*',
        rfcTableFields: '*',
        rfcTableOptions: '*',
        customRfcReadTableFunctionModule: '*',
        partitionOption: '*',
        partitionSettings: {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*',
            maxPartitionsNumber: '*'
        },
        sapDataColumnDelimiter: '*'
    }
})
export class SapTableCopySourceAdapter extends CopySourceAdapter<SapTableCopySource, Model.SapTableSource> {
    protected override async _toResource(copySource: SapTableCopySource): Promise<Model.SapTableSource> {
        const result: Model.SapTableSource = this.getCommonCopySourceProperties(
            copySource,
            Metamodel.modelFor(SapTableCopySource).name
        );

        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );
        if (exposureControlService.showSapTableRowSkip) {
            result.batchSize = ValueAdapter.toResource(copySource, SapTableCopySource.BATCH_SIZE);
            result.rowSkips = ValueAdapter.toResource(copySource, SapTableCopySource.ROW_SKIPS);
        }
        result.rowCount = ValueAdapter.toResource(copySource, SapTableCopySource.ROW_COUNT);
        result.rfcTableOptions = ValueAdapter.toResource(copySource, SapTableCopySource.RFC_TABLE_OPTIONS);
        result.customRfcReadTableFunctionModule = ValueAdapter.toResource(
            copySource,
            SapTableCopySource.CUSTOM_FUNCTION_MODULE
        );

        const rfcTableFields = SapTableCopySource.RFC_TABLE_FIELDS.templateValue(copySource);
        if (rfcTableFields !== undefined) {
            result.rfcTableFields = {
                value: rfcTableFields,
                type: 'Expression'
            };
        } else {
            if (copySource.rfcTableFields != null) {
                result.rfcTableFields = copySource.rfcTableFields.join();
            }
        }

        // partition related
        result.partitionOption = ValueAdapter.toResource(copySource, SapTableCopySource.PARTITION_OPTION);
        if (
            SapTableCopySource.PARTITION_OPTION.templateValue(copySource) != null ||
            (!stringIsNullOrEmpty(copySource.partitionOption) &&
                copySource.partitionOption !== partitionOptionConst.none)
        ) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(copySource, SapTableCopySource.PARTITION_COLUMN_NAME),
                partitionUpperBound: ValueAdapter.toResource(copySource, SapTableCopySource.PARTITION_UPPER_BOUND),
                partitionLowerBound: ValueAdapter.toResource(copySource, SapTableCopySource.PARTITION_LOWER_BOUND),
                maxPartitionsNumber: ValueAdapter.toResource(copySource, SapTableCopySource.MAX_PARTITIONS_NUMBER)
            };
        }

        const sapDataColumnDelimiter = ValueAdapter.toResource(copySource, SapTableCopySource.SAP_COLUMN_DELIMITER);
        try {
            result.sapDataColumnDelimiter = JSON.parse(JSON.stringify(sapDataColumnDelimiter).replace(/\\\\/g, '\\'));
        } catch {
            result.sapDataColumnDelimiter = sapDataColumnDelimiter;
        }
        if (result.sapDataColumnDelimiter != null && typeof result.sapDataColumnDelimiter === 'string') {
            result.sapDataColumnDelimiter = string2UnicodeSingle(result.sapDataColumnDelimiter);
        }

        return result;
    }

    protected override async _toModel(
        resource: Model.SapTableSource,
        copyActivity: CopyActivity
    ): Promise<SapTableCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            SapTableCopySource.MODEL,
            resource,
            copyActivity
        ) as SapTableCopySource;
        result.mode = 'table'; // only support table

        ValueAdapter.toModel(result, SapTableCopySource.BATCH_SIZE, resource.batchSize);
        ValueAdapter.toModel(result, SapTableCopySource.ROW_COUNT, resource.rowCount);
        ValueAdapter.toModel(result, SapTableCopySource.ROW_SKIPS, resource.rowSkips);
        ValueAdapter.toModel(result, SapTableCopySource.RFC_TABLE_OPTIONS, resource.rfcTableOptions);
        ValueAdapter.toModel(
            result,
            SapTableCopySource.CUSTOM_FUNCTION_MODULE,
            resource.customRfcReadTableFunctionModule
        );

        if (resource != null) {
            if (resource.rfcTableFields != null && resource.rfcTableFields['type'] === 'Expression') {
                SapTableCopySource.RFC_TABLE_FIELDS.setTemplateValue(result, resource.rfcTableFields.value);
            } else if (resource.rfcTableFields != null) {
                if (typeof resource.rfcTableFields === 'string' && isLogicAppExpression(resource.rfcTableFields)) {
                    SapTableCopySource.RFC_TABLE_FIELDS.setTemplateValue(result, resource.rfcTableFields);
                } else {
                    result.rfcTableFields = stringIsNullOrEmpty(resource.rfcTableFields)
                        ? []
                        : resource.rfcTableFields.split(',');
                }
            }
        }

        if (resource.partitionOption != null) {
            ValueAdapter.toModel(result, SapTableCopySource.PARTITION_OPTION, resource.partitionOption);
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                SapTableCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                SapTableCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                SapTableCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
            ValueAdapter.toModel(
                result,
                SapTableCopySource.MAX_PARTITIONS_NUMBER,
                resource.partitionSettings.maxPartitionsNumber
            );
        }

        ValueAdapter.toModel(result, SapTableCopySource.SAP_COLUMN_DELIMITER, resource.sapDataColumnDelimiter);
        if (result.sapDataColumnDelimiter != null && typeof result.sapDataColumnDelimiter === 'string') {
            result.sapDataColumnDelimiter = unicode2StrSingle(
                result.sapDataColumnDelimiter,
                columnDelimiterWithoutNoneV2Options.map((option) => option.value)
            );
            if (!result.sapDataColumnDelimiter.startsWith('\\u')) {
                // reserve \\ to avoid second escape
                result.sapDataColumnDelimiter = result.sapDataColumnDelimiter.replace(/\\/g, '\\\\');
            }
        }
        return result;
    }
}
